import { c as _c } from "react/compiler-runtime";
import type { ApiNodeDataBranch, BranchComparison, NodeConfigurationContextBranch } from '@onefootprint/request-types/dashboard';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import useStore from '@/components/playbook-builder/hooks/use-store';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../../base-node';
import type { BranchFormData } from '../../types';
import createPayload from '../../utils/create-payload';
import { FormContent } from './components/form-content';
const toFormValue = (c: BranchComparison): string => {
  if (typeof c.value === 'boolean') return c.value ? 'true' : 'false';
  if (typeof c.value === 'number') return String(c.value);
  return typeof c.value === 'string' ? c.value : JSON.stringify(c.value);
};
type BranchContentProps = {
  node: NodeProps<ApiNodeDataBranch>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
  config: NodeConfigurationContextBranch;
};
type BranchContentContainerProps = Omit<BranchContentProps, 'config'> & {
  config: NodeConfigurationContextBranch | undefined;
};
const BranchContent = t0 => {
  const $ = _c(5);
  const {
    node,
    mutation,
    canEdit,
    config
  } = t0;
  if (!config) {
    return null;
  }
  let t1;
  if ($[0] !== canEdit || $[1] !== config || $[2] !== mutation || $[3] !== node) {
    t1 = <BranchContentInner node={node} mutation={mutation} canEdit={canEdit} config={config} />;
    $[0] = canEdit;
    $[1] = config;
    $[2] = mutation;
    $[3] = node;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const BranchContentInner = t0 => {
  const $ = _c(45);
  const {
    node,
    mutation,
    canEdit,
    config
  } = t0;
  const {
    toggle
  } = useDirty();
  const {
    setNodeHighlight
  } = useStore();
  let t1;
  if ($[0] !== config.config || $[1] !== config.identifier) {
    let t2;
    if ($[3] !== config.identifier) {
      t2 = c => c.identifier === config.identifier;
      $[3] = config.identifier;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = config.config.find(t2);
    $[0] = config.config;
    $[1] = config.identifier;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currentConfig = t1;
  const t2 = currentConfig?.identifier ?? undefined;
  let t3;
  if ($[5] !== node.data.cases) {
    t3 = node.data.cases.map(_temp);
    $[5] = node.data.cases;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== node.data.defaultBranch.name || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      branchPredicateId: t2,
      cases: t3,
      defaultBranch: node.data.defaultBranch.name
    };
    $[7] = node.data.defaultBranch.name;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = !canEdit;
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = {
      defaultValues: t4,
      disabled: t5
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const formMethods = useForm(t6);
  const {
    formState,
    control
  } = formMethods;
  let t7;
  if ($[14] !== control) {
    t7 = {
      control,
      name: "branchPredicateId"
    };
    $[14] = control;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const branchPredicateId = useWatch(t7);
  let t8;
  if ($[16] !== formState.isDirty || $[17] !== toggle) {
    t8 = () => {
      toggle("branch-node", formState.isDirty);
    };
    $[16] = formState.isDirty;
    $[17] = toggle;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== formState.isDirty) {
    t9 = [formState.isDirty];
    $[19] = formState.isDirty;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  if ($[21] !== branchPredicateId || $[22] !== config.config || $[23] !== node.id || $[24] !== setNodeHighlight) {
    t10 = () => {
      if (config?.config) {
        const sourceNodeIds = config.config.find(c_1 => c_1.identifier === branchPredicateId)?.sourceNodeIds;
        if (sourceNodeIds) {
          setNodeHighlight({
            color: "info",
            ids: sourceNodeIds,
            nodeId: node.id,
            variant: "border-and-background"
          });
        }
      }
      return () => {
        setNodeHighlight(prev => prev?.nodeId === node.id ? undefined : prev);
      };
    };
    $[21] = branchPredicateId;
    $[22] = config.config;
    $[23] = node.id;
    $[24] = setNodeHighlight;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== branchPredicateId || $[27] !== node.id) {
    t11 = [node.id, branchPredicateId];
    $[26] = branchPredicateId;
    $[27] = node.id;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  useEffect(t10, t11);
  let t12;
  if ($[29] !== config || $[30] !== mutation || $[31] !== node) {
    t12 = async data => {
      if (!config) {
        console.error("Branch node config is undefined");
        return;
      }
      const payload = createPayload(data, node, config);
      await mutation.mutateAsync(payload);
    };
    $[29] = config;
    $[30] = mutation;
    $[31] = node;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const onSubmit = t12;
  let t13;
  if ($[33] !== formMethods || $[34] !== onSubmit) {
    t13 = formMethods.handleSubmit(onSubmit);
    $[33] = formMethods;
    $[34] = onSubmit;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = config?.config;
  let t15;
  if ($[36] !== canEdit || $[37] !== t14) {
    t15 = <FormContent config={t14} canEdit={canEdit} />;
    $[36] = canEdit;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t13 || $[40] !== t15) {
    t16 = <form onSubmit={t13} id="branch-node-form">{t15}</form>;
    $[39] = t13;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== formMethods || $[43] !== t16) {
    t17 = <FormProvider {...formMethods}>{t16}</FormProvider>;
    $[42] = formMethods;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
};
export default BranchContent;
function _temp(c_0) {
  return {
    branchId: c_0.id,
    branch: c_0.branch,
    condition: {
      op: c_0.condition.op,
      value: toFormValue(c_0.condition)
    }
  };
}